import { Observable, Subject } from 'rxjs';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EntityList } from 'app/models/entity-list.model';
import { map } from 'rxjs/operators';
import { User, UserInfo } from '../models/user.model';
import { toHttpParams } from './http-query-params';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private _http = inject(HttpClient);

  update = new Subject<{ operation: string; value: User[] }>();

  getUserById(userId: number): Observable<{ statusCode: number; data: User }> {
    return this._http.get<{ data: User; statusCode: number }>('/api/user/' + userId, { observe: 'response' }).pipe(map(res => res.body!));
  }

  getUserList(query: UserQuery = {}): Observable<{ statusCode: number; data: EntityList<User> }> {
    const route: Record<keyof UserQuery, string> = {
      userIds: 'ids',
      adminIds: 'admin-ids',
      offset: 'offset',
      limit: 'limit',
      exclude: 'exclude',
      like: 'like',
    };

    return this._http
      .get<{ statusCode: number; data: EntityList<User> }>('/api/user/list', { observe: 'response', params: toHttpParams(query, route) })
      .pipe(map(res => res.body!));
  }

  addUser(newUser: UserInfo): Observable<EmptyMutationResponse> {
    return this._http.post<void>('/api/user', newUser, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'addUser', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  deleteUser(userId: number): Observable<{ statusCode: number }> {
    return this._http.delete<{ statusCode: number }>('/api/user/' + userId, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'addUser', value: [] });

        return { statusCode: res.body!.statusCode };
      }),
    );
  }

  updateUser(userId: number, userData: UserInfo): Observable<{ statusCode: number; data: User }> {
    return this._http.put<{ data: User; statusCode: number }>('/api/user', { ...userData, id: userId }, { observe: 'response' }).pipe(
      map(res => {
        const body = res.body!;
        this.update.next({ operation: 'addUser', value: [body.data] });

        return body;
      }),
    );
  }

  setActivation(id: number, status: boolean): Observable<{ statusCode: number; data: User }> {
    return this._http
      .put<{ data: User; statusCode: number }>('/api/user', { isActive: status, id }, { observe: 'response' })
      .pipe(map(res => res.body!));
  }
  updateCredit(payload: { window: number; ppf: number; id: number }): Observable<{ statusCode: number; data: User }> {
    return this._http
      .put<{ data: User; statusCode: number }>('/api/user/credit', payload, { observe: 'response' })
      .pipe(map(res => res.body!));
  }
}

type EmptyMutationResponse = { statusCode: number };

const toEmptyMutationResponse = (response: HttpResponse<unknown>): EmptyMutationResponse => ({ statusCode: response.status });

export type UserQuery = {
  adminIds?: number[];
  userIds?: number[];
  offset?: number;
  limit?: number;
  like?: string;
  exclude?: number[];
};
